import { model as config } from '@/config'
import { postpone } from '@/effector'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEvent,
  createStore,
  sample,
  type Event,
  type EventCallable,
  type StoreWritable,
} from 'effector'
import { and, not, pending } from 'patronum'
import * as runtime from '~/app/environment/runtime'
import { model as launchModel } from '~/core/launch'
import { model as sessionEntityModel } from '~/entities/session'
import { preferencesFeatureModel } from '~/features/preferences'
import { session } from '~/features/session'
import type { AnyableAnalytixUserInfo } from '~/shared/analytix'
import { F, T } from '~/shared/helpers'
import { logErrorFx } from '../lib'
import { $analytix, $analytixEnabled } from '../wrapper'

export const updateAnalytixUserInfo = createEvent<AnyableAnalytixUserInfo>()

export const updateAnalytixUserInfoFx = attach({
  source: $analytix,
  effect: (analytix, patch: AnyableAnalytixUserInfo) => {
    analytix.userInfo = patch
  },
})

sample({
  clock: updateAnalytixUserInfo,
  target: updateAnalytixUserInfoFx,
})

// update analytix primaryRegion if it was changed
sample({
  clock: [$analytixEnabled, preferencesFeatureModel.$preference],
  source: {
    enabled: $analytixEnabled,
    preference: preferencesFeatureModel.$preference,
  },
  filter: ({ enabled, preference }) => enabled && Boolean(preference),
  fn: ({ preference }) => ({
    preferredRegion: preference?.name || '',
  }),
  target: updateAnalytixUserInfo,
})

// update anlytix windowSessionGuid content_user_profile_name
sample({
  clock: $analytixEnabled,
  source: session.$windowSessionGuid,
  fn: (windowSessionGuid) => ({
    contentUserProfileName: windowSessionGuid,
  }),
  target: updateAnalytixUserInfo,
})

// update anlytix sessionId event_parameter9
sample({
  clock: $analytixEnabled,
  source: session.$session,
  fn: (session) => ({
    sessionId: session?.id ?? '',
  }),
  target: updateAnalytixUserInfo,
})

sample({
  clock: [$analytixEnabled, sessionEntityModel.$info],
  source: {
    enabled: $analytixEnabled,
    info: sessionEntityModel.$info,
  },
  filter: ({ enabled, info }) => enabled && Boolean(info),
  fn: ({ info }) => ({
    networkIp: info?.ip ?? '',
    city: info?.location?.city ?? '',
    country: info?.location?.country ?? '',
    latitude: info?.location?.latitude.toString() ?? '',
    longitude: info?.location?.longitude.toString() ?? '',
    contentUserSubscriberId: runtime.isBot ? 'bot' : info?.userGuid || '',
    contentUserAccountNumber: info?.profile?.birthday,
    contentUserProfileId: info?.profile?.gender,
  }),
  target: updateAnalytixUserInfo,
})

/**
 * `contentUserSubscriberId` above is only known from `/users/info`, and sign in and
 * sign up read it back as a *second* round trip — 25-90 ms measured on uvo-dev.
 * Until that answer has reached the SDK the parameter still holds the guest id, so
 * an event sent inside the window is attributed to a guest the user has just
 * stopped being (OVP-3119). The events a transition emits are routed through
 * {@link afterSubscriberIdSettled}, which holds them until this store opens.
 *
 * Opened when a patch that carries `contentUserSubscriberId` has reached the SDK,
 * and not while `/users/info` is still in flight. `preferredRegion`, profile and
 * session patches do not refresh the id. Neither does the subscriber patch
 * `$analytixEnabled` emits from the guest `$info` during that refresh: `$info`
 * only changes on `getInfoFx.doneData`. The patch effect's `.done` is the proof
 * the SDK already holds the id — the held send is queued off this store, after
 * the handler. `getInfoFx.fail` opens the gate as well, because a failed refresh
 * leaves `$info` empty and produces no patch; without that escape one bad request
 * would silence the held events for the rest of the page's life.
 */
const $isSessionInfoPending = pending([api.userSession.getInfoFx])

const subscriberIdPatchSettled = createEvent()

sample({
  clock: updateAnalytixUserInfoFx.done,
  source: $isSessionInfoPending,
  filter: (isSessionInfoPending, { params }) =>
    !isSessionInfoPending && 'contentUserSubscriberId' in params,
  target: subscriberIdPatchSettled,
})

export const $subscriberIdSettled: StoreWritable<boolean> =
  createStore<boolean>(true)
    .on([api.user.signInFx.done, api.user.signUpFx.done], F)
    .on([subscriberIdPatchSettled, api.userSession.getInfoFx.fail], T)

/**
 * Passes `clock` through once {@link $subscriberIdSettled} is open, holding it while
 * it is closed instead of dropping it. Use it on the events an auth transition emits.
 *
 * The hold is one-shot: the pending payload is cleared when it goes out, which is why
 * `postpone` is not reused here. `postpone` keeps its last clock payload for good, and
 * this gate closes and opens on every sign in — so it would re-send an event the
 * previous session had already reported, and re-send a page the user has since left.
 */
export function afterSubscriberIdSettled<T>(clock: Event<T>): Event<T> {
  const released = createEvent<T>()
  const $held = createStore<T | null>(null).reset(released)

  sample({
    clock,
    filter: not($subscriberIdSettled),
    target: $held,
  })

  sample({
    clock,
    filter: $subscriberIdSettled,
    target: released,
  })

  sample({
    clock: $subscriberIdSettled,
    source: $held,
    filter: Boolean,
    target: released,
  })

  return released
}

export const $readyAllInfo = createStore<boolean>(false)

sample({
  clock: [
    sessionEntityModel.$info,
    config.$ready,
    launchModel.$isLaunchDataReady,
  ],
  source: {
    info: sessionEntityModel.$info,
    configReady: config.$ready,
    isLaunchDataReady: launchModel.$isLaunchDataReady,
  },
  filter: ({ info, configReady, isLaunchDataReady }) =>
    Boolean(info) && Boolean(configReady) && Boolean(isLaunchDataReady),
  fn: T,
  target: $readyAllInfo,
})

/**
 * Fired when session info is refreshed and analytix is ready (see `postpone` below).
 * Same timing contract as legacy OVP `sendAnalyticsSessionStart`.
 */
export const awaited: EventCallable<void> = createEvent()

export const sendSessionStartFx = attach({
  source: $analytix,
  effect: (analytix, params: { message?: string }) =>
    analytix.sendSessionStartEvent(params),
})

sample({
  clock: sendSessionStartFx.failData,
  fn: (e) => e,
  target: logErrorFx,
})

postpone({
  clock: session.updateAllSessionInfo,
  until: and($readyAllInfo, $analytixEnabled),
  target: awaited,
})

sample({
  clock: awaited,
  source: launchModel.$customErrorCode,
  fn: (customErrorCode) => ({ message: customErrorCode }),
  target: sendSessionStartFx,
})
